import { useState } from "react";
import type { Account, DashboardData, ExpenseInput, Owner } from "../../types";
import { useIdempotentSave } from "../../hooks/use-idempotent-save";
import { enqueuePendingRecord, saveLoanOrQueue, saveRecordOrQueue } from "../../lib/pending-records";

/**
 * The entry form's loan option: an entry can start a loan with its payee, or pay one off, instead
 * of being an ordinary expense or income. Saved through the loan endpoints rather than as an entry.
 */
export function useLoanEntry({ data, input, account, isIncome, profile }: {
  data: DashboardData;
  input: ExpenseInput;
  account: Account | undefined;
  isIncome: boolean;
  profile: Owner;
}) {
  // Loans created here get the same retained request key the IOU screen uses: a duplicate loan
  // brings a duplicate cash leg with it.
  const saveOnce = useIdempotentSave();
  // "" = an ordinary entry, "new" = starts a loan, "settle" = pays one off.
  const [iouMode, setIouMode] = useState<"" | "new" | "settle">("");
  const [iouChoice, setIouChoice] = useState(""); // the open IOU this settles
  const [iouDescription, setIouDescription] = useState("");
  const [iouDueDate, setIouDueDate] = useState("");
  // A new IOU funded by an Expense is money we lent (Owed to us); funded by an Income it's money
  // we borrowed (We owe). Settling an existing IOU takes the opposite type from funding it.
  const newIouDirection = isIncome ? "We owe" : "Owed to us";
  const settleIouDirection = isIncome ? "Owed to us" : "We owe";
  // Only loans in the chosen account's currency: the settlement is written as a transaction on
  // that account, so paying a USD loan from an MVR one would knock a figure off that never moved.
  const openIouMatches = input.payeeId && !input.payeeId.startsWith("local:")
    ? (data.ious || []).filter((i) => i.payeeId === input.payeeId && i.status === "Open" && i.direction === settleIouDirection && (!account || i.currency === account.currency))
    : [];

  /** A change of entry type drops the loan it was settling, and a transfer cannot be a loan at all. */
  function resetFor(type: "Expense" | "Income" | "Transfer") {
    setIouChoice("");
    if (type === "Transfer") {
      setIouMode("");
      setIouDescription("");
      setIouDueDate("");
    }
  }
  /** What still has to be filled in before the loan can be saved, or "" when nothing does. */
  function missing() {
    if (!input.payeeId && !input.payeeName) return "Choose or add a payee.";
    if (iouMode === "new" && !iouDescription.trim()) return "Add a short description.";
    if (iouMode === "settle" && !iouChoice) return "Choose which loan this pays off.";
    if (iouMode === "settle" && iouChoice.startsWith("local:"))
      return "Wait for the IOU to sync before recording a repayment.";
    return "";
  }
  async function save(): Promise<{ queued: boolean }> {
    if (iouMode === "new") {
      const draft = {
        payeeId: input.payeeId || "",
        payeeName: input.payeeName,
        direction: newIouDirection as "Owed to us" | "We owe",
        originalAmount: input.amount,
        description: iouDescription,
        createdDate: input.date,
        dueDate: iouDueDate,
        fundingAccountId: input.accountId,
      };
      return saveLoanOrQueue(profile, draft, (ready) => saveOnce("ious/create", ready));
    }
    const body = {
      iouId: iouChoice,
      amount: input.amount,
      date: input.date,
      accountId: input.accountId,
      notes: input.notes,
    };
    const id = crypto.randomUUID();
    const result = await saveRecordOrQueue(
      () => saveOnce("ious/settle", body),
      () => enqueuePendingRecord({ kind: "settle", id, owner: profile, body }),
    );
    return { queued: result.queued };
  }
  return {
    isIOU: iouMode !== "",
    fields: {
      iouMode, setIouMode, iouDescription, setIouDescription,
      iouDueDate, setIouDueDate, iouChoice, setIouChoice, openIouMatches,
    },
    resetFor, missing, save,
  };
}
